/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import { useDebounce } from '@uidotdev/usehooks';
import { useEffect } from 'react';

import { FlatSubtenant } from '@/client';
import { SubtenantSwitcherInput } from '@/components/SubtenantSwitcher/SubtenantSwitcherInput';
import { SubtenantSwitcherSelectedSubtenant } from '@/components/SubtenantSwitcher/SubtenantSwitcherSelectedSubtenant';
import { useDashboardVisibility } from '@/hooks/useDashboardVisibility';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';

import './SubtenantSwitcherOption.css';

export type SubtenantSwitcherOptionProps = {
  selectedSubtenant?: FlatSubtenant;
  updateSearchTerm: (searchTerm: string) => void;
  showInput: boolean;
  setShowInput: (showInput: boolean) => void;
  handlePressEnter: (term: string) => void;
  inputSearch: string;
  setInputSearch: (val: string) => void;
};

export function SubtenantSwitcherOption({
  selectedSubtenant,
  updateSearchTerm,
  showInput,
  setShowInput,
  handlePressEnter,
  inputSearch,
  setInputSearch,
}: SubtenantSwitcherOptionProps) {
  const debouncedSearchTerm = useDebounce(inputSearch, 300);
  const dark = useMgmtPlaneStateStore((state) => state.darkMode);
  const className = dark ? 'dark' : 'light';
  const isDashboardHidden = useDashboardVisibility();

  const handleOnBlur = () => {
    if (isDashboardHidden()) return; // Prevent blur action if dashboard is hidden
    setShowInput(false);
  };

  useEffect(() => {
    updateSearchTerm(debouncedSearchTerm);
  }, [debouncedSearchTerm, updateSearchTerm]);

  const classes = ['subtenant-switcher-option', className];

  if (showInput) {
    if (dark) classes.push('show-input-dark');
    if (!dark) classes.push('show-input-light');
  }

  return (
    <section className={classes.join(' ')}>
      {!showInput && (
        <SubtenantSwitcherSelectedSubtenant selectedSubtenant={selectedSubtenant} setShowInput={setShowInput} />
      )}
      {showInput && (
        <SubtenantSwitcherInput
          inputSearch={inputSearch}
          setInputSearch={setInputSearch}
          handleOnBlur={handleOnBlur}
          handlePressEnter={handlePressEnter}
        />
      )}
    </section>
  );
}
